import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, MoreVertical, RefreshCcw, Settings } from "lucide-react";

// Kebab-menu that renders its dropdown through a portal into document.body,
// so it always overlays table rows/cells instead of being clipped by the
// table's overflow-x-auto wrapper. Position is recalculated relative to the
// trigger button and flips above/below & left/right based on viewport space.
const OrderActionMenu = ({ order, onViewItems, onUpdateStatus }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ vertical: "bottom", horizontal: "right", style: {} });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const calculatePosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = 180;
    const dropdownWidth = 240;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    const style = { position: "fixed", zIndex: 9999 };
    let vertical = "bottom";

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      vertical = "top";
      style.bottom = `${viewportHeight - rect.top + 8}px`;
    } else {
      style.top = `${rect.bottom + 8}px`;
    }

    let horizontal = "right";
    style.right = `${viewportWidth - rect.right}px`;
    if (spaceLeft < dropdownWidth && spaceRight > spaceLeft) {
      horizontal = "left";
      style.right = "auto";
      style.left = `${rect.left}px`;
    }

    setPosition({ vertical, horizontal, style });
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    calculatePosition();

    const handleReposition = () => calculatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      const outsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const outsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      if (outsideButton && outsideDropdown) setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const actions = [
    {
      key: "view",
      label: "View Order Items",
      description: "See purchased items, pricing & customer info",
      icon: <Eye size={16} className="text-blue-600" />,
      onClick: () => onViewItems(order),
    },
    {
      key: "status",
      label: "Update Status",
      description: "Change this order's fulfillment status",
      icon: <RefreshCcw size={16} className="text-[#FF6C6C]" />,
      onClick: () => onUpdateStatus(order),
    },
  ];

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((prev) => !prev)}
        className="p-1.5 rounded-lg transition-all hover:bg-gray-100 hover:shadow-sm active:scale-95"
        aria-label="Order actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <MoreVertical size={16} className="text-gray-500" />
      </button>

      {isOpen &&
        createPortal(
          <AnimatePresence>
            <motion.div
              ref={dropdownRef}
              role="menu"
              initial={{ opacity: 0, scale: 0.95, y: position.vertical === "top" ? 10 : -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: position.vertical === "top" ? 10 : -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              style={position.style}
              className="overflow-hidden w-60 bg-white rounded-xl border border-gray-200 shadow-2xl max-w-[calc(100vw-2rem)]"
            >
              <div className="flex gap-2 items-center px-3 py-2.5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <Settings className="w-4 h-4 text-gray-500" />
                <span className="text-[11px] font-semibold tracking-wide text-gray-600 uppercase">
                  Order Actions
                </span>
              </div>
              <div className="py-1">
                {actions.map((action) => (
                  <button
                    key={action.key}
                    role="menuitem"
                    onClick={() => {
                      action.onClick();
                      setIsOpen(false);
                    }}
                    className="flex gap-3 items-start px-3 py-2.5 w-full text-left transition-colors hover:bg-gray-50 group"
                  >
                    <div className="mt-0.5 flex-shrink-0 transition-transform group-hover:scale-110">
                      {action.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800">{action.label}</div>
                      <div className="text-xs text-gray-500 leading-snug">{action.description}</div>
                    </div>
                  </button>
                ))}
              </div>
              <div
                className="absolute w-2 h-2 bg-white border-gray-200 pointer-events-none"
                style={{
                  [position.vertical === "top" ? "bottom" : "top"]: "-4px",
                  [position.horizontal]: "12px",
                  transform: "rotate(45deg)",
                  borderWidth: position.vertical === "top" ? "0 1px 1px 0" : "1px 0 0 1px",
                }}
              />
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

export default OrderActionMenu;
